import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Upload, ArrowDownToLine } from "lucide-react";
import { PageHeader, Loader, Badge, Empty } from "@/components/shared";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, apiError } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { fmtPKR, fmtDate, statusColor } from "@/lib/format";

const FALLBACK = "Payment account details will be shown here.";

export default function Deposit() {
  const { t } = useI18n();
  const [deposits, setDeposits] = useState(null);
  const [methods, setMethods] = useState([]);
  const [form, setForm] = useState({ method: "", amount: "", transaction_id: "" });
  const [slip, setSlip] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/deposits").then(({ data }) => setDeposits(data)).catch(() => {});
  const [cap, setCap] = useState(null);
  useEffect(() => {
    load();
    api.get("/payment-methods").then(({ data }) => {
      setMethods(data);
      if (data.length) setForm((f) => ({ ...f, method: data[0].name }));
    }).catch(() => {});
    api.get("/settings/app").then(({ data }) => setCap(data)).catch(() => {});
  }, []);
  if (!deposits) return <Loader />;

  const activeDetails = methods.find((m) => m.name === form.method)?.details || FALLBACK;

  const submit = async (e) => {
    e.preventDefault();
    if (!slip) { toast.error("Please upload your payment slip"); return; }
    if (!form.amount || Number(form.amount) <= 0) { toast.error("Enter a valid amount"); return; }
    if (Number(form.amount) < 1000) { toast.error("Minimum deposit amount is Rs 1,000"); return; }
    if (!form.method) { toast.error("Select a payment method"); return; }
    setBusy(true);
    const fd = new FormData();
    fd.append("method", form.method); fd.append("amount", form.amount);
    fd.append("transaction_id", form.transaction_id); fd.append("slip", slip);
    try {
      await api.post("/deposits", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Deposit submitted for review!");
      setForm({ method: methods[0]?.name || "", amount: "", transaction_id: "" }); setSlip(null); load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div>
      <PageHeader title={t("deposit")} subtitle="Top up your balance. Deposits are credited after admin approval." />
      {cap && <p className="text-xs text-gray-400 mb-4" data-testid="deposit-cap-note">Deposit requests used: {cap.deposits_used} / {cap.deposit_cap}</p>}
      <div className="grid lg:grid-cols-2 gap-8 items-start">
        <form onSubmit={submit} className="ec-card p-6 space-y-4">
          <div>
            <Label>{t("payment_method")}</Label>
            <Select value={form.method} onValueChange={(v) => setForm({ ...form, method: v })}>
              <SelectTrigger className="mt-1 bg-white/5 border-white/10" data-testid="deposit-method"><SelectValue placeholder="Select method" /></SelectTrigger>
              <SelectContent>{methods.map((m) => <SelectItem key={m.id} value={m.name}>{m.name}</SelectItem>)}</SelectContent>
            </Select>
            <p className="text-xs text-orange-400 mt-2 bg-orange-500/10 border border-orange-500/20 rounded-lg p-2 keep-ltr whitespace-pre-wrap" data-testid="deposit-account-hint">{activeDetails}</p>
          </div>
          <div><Label>{t("request_amount")} (PKR)</Label><Input data-testid="deposit-amount" type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} className="mt-1" placeholder="Minimum Rs 1,000" /></div>
          <div><Label>{t("txn_id")}</Label><Input data-testid="deposit-txnid" value={form.transaction_id} onChange={(e) => setForm({ ...form, transaction_id: e.target.value })} required className="mt-1" placeholder="e.g. TXN123456789" /></div>
          <div>
            <Label>{t("upload_slip")}</Label>
            <label className="mt-1 flex items-center gap-3 px-4 py-3 rounded-xl border border-dashed border-white/15 bg-white/5 cursor-pointer hover:border-orange-500/50 transition-colors">
              <Upload className="w-5 h-5 text-orange-400" />
              <span className="text-sm text-gray-400 truncate" data-testid="deposit-slip-name">{slip?.name || "Choose from Gallery / Device"}</span>
              <input type="file" accept="image/*,.pdf" className="hidden" data-testid="deposit-slip" onChange={(e) => setSlip(e.target.files[0])} />
            </label>
          </div>
          <Button type="submit" disabled={busy} data-testid="deposit-submit" className="w-full bg-orange-600 hover:bg-orange-500 btn-glow font-semibold"><ArrowDownToLine className="w-4 h-4 me-2" />{busy ? "..." : t("submit_deposit")}</Button>
        </form>

        <div>
          <h3 className="font-head text-lg font-semibold text-white mb-4">{t("my_deposits")}</h3>
          {deposits.length === 0 ? <Empty label="No deposits yet." /> : (
            <div className="space-y-3">
              {deposits.map((d) => (
                <div key={d.id} className="ec-card p-4" data-testid={`deposit-${d.id}`}>
                  <div className="flex items-center justify-between">
                    <div><p className="font-semibold text-white">{fmtPKR(d.amount)}</p><p className="text-xs text-gray-500 keep-ltr">{d.method} · {d.transaction_id}</p></div>
                    <Badge className={statusColor(d.status)}>{d.status}</Badge>
                  </div>
                  {d.reason && <p className="text-xs text-red-400 mt-2">{d.reason}</p>}
                  <p className="text-xs text-gray-600 mt-1">{fmtDate(d.created_at)}</p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
